import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Truck, Users, Target, Wrench, BadgeCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader, CtaButton } from "@/components/Bits";
import { LEISTUNGEN, BRANDS } from "@/data/catalog";

const IMG =
  "https://images.unsplash.com/photo-1562052579-b30d05e1ec93?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const ICONS = { "LST-01": Users, "LST-02": Truck, "LST-03": ShieldCheck, "LST-04": Target, "LST-05": Wrench, "LST-06": BadgeCheck };

export default function Fachhandel() {
  return (
    <>
      <Seo
        title="Fachhandel"
        description="IMPEXMILTRADE GMBH — konzessionierter Waffenfachhandel in Mureck. Verkauf, Beratung, Import & Export, Belieferung von Behörden, Händlern, Jägern und Sportschützen."
      />
      <PageHeader
        code="INFO / Über den Betrieb"
        title="Fachhandel"
        sub="Ein konzessionierter Waffenfachhandel mit klarem Anspruch: sachkundige Beratung, saubere Abwicklung und ein Sortiment, das Sport, Jagd und Behörde gleichermaßen abdeckt."
      />

      <section className="mx-auto grid max-w-7xl border-b border-border lg:grid-cols-2" data-testid="fachhandel-intro">
        <div className="border-r border-border p-5 py-14 md:p-14">
          <p className="mono-label text-primary">Der Betrieb</p>
          <h2 className="mt-4 font-display text-3xl font-600 uppercase tracking-tight md:text-4xl">
            Kompetenz aus einer Hand
          </h2>
          <div className="mt-6 space-y-4 font-body text-muted-foreground">
            <p>
              Als eingetragene GmbH mit Sitz am Hauptplatz in Mureck sind wir Ansprechpartner für alle,
              die Wert auf fachkundige Beratung legen. Wir verstehen unser Handwerk — von der ersten
              Frage bis zur rechtskonformen Übergabe.
            </p>
            <p>
              Unser Sortiment umfasst Kurz- und Langwaffen, Munition in allen gängigen Kalibern sowie
              das passende Zubehör. Was nicht vorrätig ist, beschaffen wir zuverlässig über unser
              Hersteller- und Händlernetz.
            </p>
            <p>
              Import, Export und die Verbringung von Waffen wickeln wir inklusive sämtlicher
              Genehmigungen und Dokumentation ab — für Privatkunden, Händler und Behörden.
            </p>
          </div>
        </div>
        <div className="relative min-h-[300px] overflow-hidden">
          <img src={IMG} alt="Präzisionsfertigung" className="h-full w-full object-cover" />
          <div className="blueprint-grid absolute inset-0 opacity-30 mix-blend-overlay" aria-hidden />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16" data-testid="fachhandel-leistungen">
        <div className="border-b border-border pb-4">
          <p className="mono-label text-primary">Leistungen</p>
          <h2 className="mt-3 font-display text-3xl font-600 uppercase tracking-tight md:text-4xl">Für wen wir arbeiten</h2>
        </div>
        <div className="grid border-l border-t border-border md:grid-cols-2">
          {LEISTUNGEN.map((l, i) => {
            const Icon = ICONS[l.code] || Target;
            return (
              <motion.div
                key={l.code}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: (i % 2) * 0.06 }}
                className="border-b border-r border-border p-8"
                data-testid={`leistung-${l.code}`}
              >
                <div className="flex items-center justify-between">
                  <Icon className="text-primary" size={28} strokeWidth={1.5} />
                  <span className="mono-label text-muted-foreground">{l.code}</span>
                </div>
                <h3 className="mt-6 font-display text-2xl font-500 uppercase tracking-tight">{l.title}</h3>
                <p className="mt-3 font-body text-sm text-muted-foreground">{l.copy}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Dealer / B2B */}
      <section className="border-y border-border bg-card" data-testid="fachhandel-dealer">
        <div className="mx-auto max-w-7xl px-5 py-16 md:py-24">
          <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <p className="mono-label text-primary">B2B · Fachhändler</p>
              <h2 className="mt-4 font-display text-3xl font-600 uppercase tracking-tight md:text-4xl">
                Händler & Behörden
              </h2>
              <p className="mt-6 max-w-md font-body text-muted-foreground">
                Registrierte Fachhändler, Sicherheitsdienste und Behörden erhalten Zugang zu
                Konditionen, Verfügbarkeiten und Sonderbeschaffung. Kontaktieren Sie uns für einen
                Dealer-Zugang.
              </p>
              <div className="mt-8">
                <CtaButton to="/kontakt" testid="dealer-cta">
                  Dealer-Zugang anfragen <ArrowRight size={16} />
                </CtaButton>
              </div>
            </div>
            <div className="border border-border">
              <p className="mono-label border-b border-border p-4 text-muted-foreground">Marken im Programm</p>
              <div className="grid grid-cols-2 sm:grid-cols-3">
                {BRANDS.map((b, i) => (
                  <span
                    key={b}
                    className={`p-4 font-display text-sm font-500 uppercase tracking-wide text-foreground ${
                      (i + 1) % 3 !== 0 ? "sm:border-r" : ""
                    } ${i % 2 === 0 ? "border-r sm:border-r-0" : ""} border-b border-border`}
                  >
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
